import UlakLogo from "../assets/images/companies/ulak.jpg";
import HirehootLogo from "../assets/images/companies/hirehoot.jpg";
import SvaypLogo from "../assets/images/companies/svayp.jpg";
import ImzaLogo from "../assets/images/companies/imza.jpg";
import WesteropsLogo from "../assets/images/companies/westerops.jpg";

// Selected professional work, newest first. The numbers come from the shipped
// projects — keep them accurate, they are the whole point of this section.
// Language-neutral fields (logo, dates, metric values, stack) live at the top
// level; anything with prose is mirrored under `en` and `tr`.
const caseStudies = [
  {
    id: "ulak",
    company: "Ulak CX",
    logo: UlakLogo,
    period: { en: "Dec 2025 — Present", tr: "Ara 2025 — Halen" },
    link: "https://ulakcx.com",
    stack: {
      en: ["AI integration", "React", "Internal tools"],
      tr: ["Yapay zekâ entegrasyonu", "React", "Kurum içi araçlar"],
    },
    en: {
      role: "Computer Engineer",
      location: "Siirt · On-site",
      tagline: "AI for call-centre operations",
      problem:
        "Quality evaluation meant a human listening to calls one by one. It did not scale with the floor.",
      work: [
        "Built a system that scores call quality automatically with AI, replacing manual review",
        "Shipped an AI assistant that gives agents feedback on their calls",
        "Wrote the canteen and market stock system: staff pay by card, managers get detailed reporting",
      ],
      metrics: [
        { value: "Automated", label: "quality evaluation" },
        { value: "AI", label: "agent feedback" },
        { value: "In-house", label: "stock & reporting" },
      ],
    },
    tr: {
      role: "Bilgisayar Mühendisi",
      location: "Siirt · Yerinde",
      tagline: "Çağrı merkezi operasyonları için yapay zekâ",
      problem:
        "Kalite değerlendirmesi, bir kişinin çağrıları tek tek dinlemesi demekti. Çağrı hacmiyle birlikte ölçeklenmiyordu.",
      work: [
        "Çağrı kalitesini yapay zekâ ile otomatik puanlayan, manuel incelemenin yerini alan bir sistem geliştirdim",
        "Personele çağrıları hakkında geri bildirim veren bir yapay zekâ asistanı geliştirdim",
        "Yemekhane ve market için stok takip sistemi yazdım: personel kartıyla ödüyor, yöneticiler detaylı rapor alıyor",
      ],
      metrics: [
        { value: "Otomatik", label: "kalite değerlendirme" },
        { value: "Yapay zekâ", label: "personel geri bildirimi" },
        { value: "Kurum içi", label: "stok & raporlama" },
      ],
    },
  },
  {
    id: "hirehoot",
    company: "Hirehoot",
    logo: HirehootLogo,
    period: { en: "Feb 2025 — Present", tr: "Şub 2025 — Halen" },
    link: "https://hirehoot.net/",
    stack: {
      en: ["React", "TypeScript", "LLM APIs"],
      tr: ["React", "TypeScript", "LLM API'leri"],
    },
    en: {
      role: "Frontend Developer",
      location: "United Kingdom · Remote",
      tagline: "AI-assisted hiring",
      problem:
        "Screening CVs by hand is the slowest part of hiring, and the parsing has to be right or the shortlist is wrong.",
      work: [
        "Built out the app side of the frontend with scalability in mind",
        "Worked on AI-assisted CV analysis, parsing and document processing",
        "Wired LLM tooling into the workflow and surfaced it in the interface",
      ],
      metrics: [
        { value: "LLM", label: "workflow integration" },
        { value: "CV parsing", label: "document pipeline" },
        { value: "App", label: "frontend ownership" },
      ],
    },
    tr: {
      role: "Frontend Geliştirici",
      location: "Birleşik Krallık · Uzaktan",
      tagline: "Yapay zekâ destekli işe alım",
      problem:
        "CV'leri elle taramak işe alımın en yavaş kısmı ve ayrıştırma doğru olmazsa aday listesi de yanlış çıkıyor.",
      work: [
        "Uygulamanın frontend tarafını ölçeklenebilirliği gözeterek geliştirdim",
        "Yapay zekâ destekli CV analizi, ayrıştırma ve belge işleme süreçleri üzerinde çalıştım",
        "LLM araçlarını iş akışına entegre ettim ve arayüzde kullanılabilir hale getirdim",
      ],
      metrics: [
        { value: "LLM", label: "iş akışı entegrasyonu" },
        { value: "CV ayrıştırma", label: "belge işleme" },
        { value: "Uygulama", label: "frontend sorumluluğu" },
      ],
    },
  },
  {
    id: "westerops",
    company: "WesterOps",
    logo: WesteropsLogo,
    period: { en: "Jun 2024 — Jan 2025", tr: "Haz 2024 — Oca 2025" },
    link: "https://westerops.com",
    stack: {
      en: ["Next.js", "TypeScript", "TailwindCSS", "GraphQL"],
      tr: ["Next.js", "TypeScript", "TailwindCSS", "GraphQL"],
    },
    en: {
      role: "Frontend Developer",
      location: "Istanbul · Remote",
      tagline: "EGN admin dashboard",
      problem:
        "An admin panel for data visualisation, user and content management — built with a full product team, so the frontend had to hold its own.",
      work: [
        "Built data visualisation, user and content management features in Next.js and TypeScript",
        "Refactored code and integrated features efficiently to speed the panel up",
        "Took a lead role in the GraphQL API integration",
      ],
      metrics: [
        { value: "−20%", label: "load time" },
        { value: "7", label: "person product team" },
        { value: "GraphQL", label: "API integration" },
      ],
    },
    tr: {
      role: "Frontend Geliştirici",
      location: "İstanbul · Uzaktan",
      tagline: "EGN yönetim paneli",
      problem:
        "Veri görselleştirme, kullanıcı ve içerik yönetimi için bir yönetim paneli — tam bir ürün ekibiyle geliştirildiği için frontend'in de aynı seviyede olması gerekiyordu.",
      work: [
        "Next.js ve TypeScript ile veri görselleştirme, kullanıcı ve içerik yönetimi özellikleri geliştirdim",
        "Kodu yeniden düzenleyip özellikleri verimli entegre ederek paneli hızlandırdım",
        "GraphQL API entegrasyonunda başrol üstlendim",
      ],
      metrics: [
        { value: "−%20", label: "yüklenme süresi" },
        { value: "7", label: "kişilik ürün ekibi" },
        { value: "GraphQL", label: "API entegrasyonu" },
      ],
    },
  },
  {
    id: "imza",
    company: "İmza Group",
    logo: ImzaLogo,
    period: { en: "Dec 2023 — Nov 2024", tr: "Ara 2023 — Kas 2024" },
    link: "https://eimzagroup.com",
    stack: {
      en: ["React", "Expo", "React Native", "Figma"],
      tr: ["React", "Expo", "React Native", "Figma"],
    },
    en: {
      role: "Frontend Developer",
      location: "Istanbul · Remote",
      tagline: "Accounting suite — web & mobile",
      problem:
        "Muhasebia took over four seconds to open and the Faturabia app was an 89 MB download. Accountants gave up before the first screen.",
      work: [
        "Designed Muhasebia end to end in Figma, then built the frontend in React",
        "Built the Faturabia mobile app with Expo and cut the bundle down aggressively",
        "Restructured the frontend architecture so both products could keep growing",
      ],
      metrics: [
        { value: "4.2s → 1.1s", label: "page load (−74%)" },
        { value: "89MB → 34MB", label: "app size (−62%)" },
        { value: "2", label: "products shipped" },
      ],
    },
    tr: {
      role: "Frontend Geliştirici",
      location: "İstanbul · Uzaktan",
      tagline: "Muhasebe çözümleri — web & mobil",
      problem:
        "Muhasebia'nın açılması dört saniyeden uzun sürüyor, Faturabia uygulaması ise 89 MB'lık bir indirme demekti. Muhasebeciler ilk ekranı görmeden vazgeçiyordu.",
      work: [
        "Muhasebia'yı baştan sona Figma'da tasarladım, ardından frontend'ini React ile geliştirdim",
        "Faturabia mobil uygulamasını Expo ile geliştirdim ve paket boyutunu ciddi biçimde küçülttüm",
        "Her iki ürünün de büyümeye devam edebilmesi için frontend mimarisini yeniden kurguladım",
      ],
      metrics: [
        { value: "4,2sn → 1,1sn", label: "sayfa yüklenmesi (−%74)" },
        { value: "89MB → 34MB", label: "uygulama boyutu (−%62)" },
        { value: "2", label: "ürün geliştirildi" },
      ],
    },
  },
  {
    id: "svayp",
    company: "Svayp",
    logo: SvaypLogo,
    period: { en: "Mar 2023 — Apr 2025", tr: "Mar 2023 — Nis 2025" },
    link: "https://svayp.com",
    stack: {
      en: ["React", "WebSocket", "TypeScript", "Dashboard UI"],
      tr: ["React", "WebSocket", "TypeScript", "Panel arayüzü"],
    },
    en: {
      role: "Frontend Web Developer",
      location: "Singapore · Remote",
      tagline: "Real-time polling platform",
      problem:
        "People opened a poll, waited for results that never moved, and left. Almost nine in ten never made it to the end.",
      work: [
        "Streamed live poll results over WebSocket so the screen reacted the moment a vote landed",
        "Designed and shipped 15 new dashboard screens with full backend integration",
        "Used AI to generate cards and summarise statistics, turning raw counts into readable insight",
      ],
      metrics: [
        { value: "87% → 23%", label: "drop-off rate" },
        { value: "15", label: "dashboard screens" },
        { value: "Real-time", label: "WebSocket results" },
      ],
    },
    tr: {
      role: "Frontend Web Geliştirici",
      location: "Singapur · Uzaktan",
      tagline: "Gerçek zamanlı anket platformu",
      problem:
        "Kullanıcılar anketi açıyor, hiç güncellenmeyen sonuçları bekliyor ve çıkıyordu. On kişiden neredeyse dokuzu sonuna kadar gelmiyordu.",
      work: [
        "Anket sonuçlarını WebSocket ile canlı akıttım; ekran, oy verildiği anda tepki verir hale geldi",
        "15 yeni panel ekranını tasarlayıp backend entegrasyonuyla birlikte geliştirdim",
        "Kart oluşturma ve istatistik özetlemede yapay zekâ kullanarak ham sayıları okunabilir içgörüye dönüştürdüm",
      ],
      metrics: [
        { value: "%87 → %23", label: "çıkış oranı" },
        { value: "15", label: "panel ekranı" },
        { value: "Gerçek zamanlı", label: "WebSocket sonuçları" },
      ],
    },
  },
];

export default caseStudies;
